import './tailwind.css';

import { useSWEffect } from '@remix-pwa/sw';
import type { LinksFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import type { ClientLoaderFunctionArgs } from '@remix-run/react';
import { Outlet, isRouteErrorResponse, useMatches, useRouteError } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';

import getSignupWidgetConfigs from '~/pages/index/_utils/getSignupWidgetConfigs';

import Document from '~/components/Document';
import FallBack from '~/components/Fallback';
import Footer from '~/components/Footer';
import * as Layout from '~/components/Layout';
import NavTemplates from '~/components/Navbar';

import useCreateVisitToken from '~/hooks/useLoaders/useCreateVisitToken';
import useNotifications from '~/hooks/useNotifications';
import useCookies from '~/hooks/useUtils/useCookies';
import useLayout from '~/hooks/useUtils/useLayout';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';

import getPublication from '~/services/api/publication/getPublication.server';
import getWebTheme from '~/services/api/web_theme/getWebTheme.server';

import getRootStyles from '~/utils/getRootStyles';

import type { AxiosError } from 'axios';
import { authSession, loaderSession } from '~/session';
import AdminBadge from './components/AdminBadge';
import { getAppGlobals } from './components/AppGlobals/utils';
import BeehiivHoney from './components/CookieBanner';
import GlobalModals from './components/GlobalModals';
import { WebThemeProvider } from './components/Layout/Context/WebThemeContext';
import EnablePushNotificationPrompt from './components/PWA/EnablePushNotificationPrompt';
import InstallPrompt from './components/PWA/InstallPrompt';
import type { SignupStyles } from './components/Signup/types';
import { PageProvider } from './context/PageProvider';
import { env } from './env.server';
import { useDisplayMode } from './hooks/useDisplayMode';
import { usePushNotification } from './hooks/usePushNotification';
import useRemoveParam from './hooks/useRemoveParam';
import useSetLastViewedResource from './hooks/useSetLastViewedResource';
import useJSDisabledSubmissionToasts from './hooks/useSubmissionErrorToast';
import useRedirectToasts from './hooks/useUtils/useRedirectToasts';
import useSiteLanguage from './hooks/useUtils/useSiteLanguage';
import useTypedLoaderData from './hooks/useUtils/useTypedLoaderData';
import useUserStatus from './hooks/useUtils/useUserStatus';
import useWebBuilderCommunicator from './hooks/useUtils/useWebBuilderCommunicator';
import type { Admin } from './interfaces/admin';
import type { Publication, WebThemeV2 } from './interfaces/publication';
import type { Subscription } from './interfaces/subscriptions';
import getAdmin from './services/api/admin/getAdmin.server';
import getSubscriptionProfile from './services/api/subscription/getSubsciptionProfile.server';
import getWebThemev2 from './services/api/web_templates/getWebThemev2.server';
import getExamplePublication from './services/exampleSite/getExamplePublication.server';
import previewAllowedCheck from './utils/previewAllowedCheck';
import { shouldRevalidateLoaderData } from './utils/shouldRevalidateLoaderData';
import { updateSessionToken } from './utils/updateSessionToken';

import { ClientOnly } from 'remix-utils/client-only';
import { BirdieLoader } from '~/components/BirdieLoader';

const appGlobals = getAppGlobals();

export const links: LinksFunction = () => {
  return [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
  ];
};

export const handle = {
  // In the handle export, we could add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  i18n: ['common'],
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (data?.publication?.name) {
    const keywords = data?.publication.tags.map((tag: any) => tag.name).join(', ');

    const allStrings = [
      `https://fonts.googleapis.com/css2?family=${data?.publication?.web_theme?.header_font
        .split(' ')
        .join('+')}&display=swap`,
      `https://fonts.googleapis.com/css2?family=${data?.publication?.web_theme?.body_font
        .split(' ')
        .join('+')}&display=swap`,
      `https://fonts.googleapis.com/css2?family=${data?.publication?.web_theme?.button_font
        .split(' ')
        .join('+')}&display=swap`,
      ...data?.publication?.web_template?.global_fonts,
    ];

    const uniqueStrings = [...new Set(allStrings)].map((url: any) => {
      return { tagName: 'link', rel: 'stylesheet', href: url };
    });

    const preloadFonts = [...new Set(allStrings)].map((url: any) => {
      return { tagName: 'link', rel: 'preload', href: url, as: 'style' };
    });

    const metaData: Array<any> = [
      { title: data?.publication.name },
      { keywords: keywords },
      { author: data?.publication.email_sender_name },
      { description: data?.publication.description },
      {
        tagName: 'link',
        rel: 'icon',
        href: data?.publication?.logo?.thumb?.url,
        as: 'image',
      },
      ...uniqueStrings,
      ...preloadFonts,
    ];

    if (data?.publication.noindex_enabled) {
      metaData.push({ robots: 'noindex' });
    }

    // add rss feed auto-discovery
    if (data?.publication?.rss_feeds?.length) {
      metaData.push({
        tagName: 'link',
        rel: 'alternate',
        type: 'application/rss+xml',
        title: `${data?.publication.name} RSS Feed`,
        href: data?.publication.rss_feeds[0].url,
      });
    }

    return metaData;
  }

  // default meta tags
  return [
    { title: 'beehiiv' },
    { description: 'The newsletter platform built for growth.' },
    { keywords: 'Newsletter, Email, Growth, Platform, SaaS, Creator' },
  ];
};

export const loader: LoaderFunction = async ({ request }) => {
  const { cookieHeaders, session } = await loaderSession(request);
  const url = new URL(request.url);

  const searchParams = url.searchParams;
  // If the request has cache buster, remove it and redirect without cache
  if (searchParams.has('__cb')) {
    searchParams.delete('__cb');
    throw redirect(`${url.pathname}${url.search}`, {
      headers: { 'cache-control': 'no-store' },
    });
  }
  const noScript = searchParams.get('no_script') === 'true';

  const token = searchParams.get('jwt_token') || null;
  const adminToken = searchParams.get('admin_token') || null;
  const forceNewWebBuilder = searchParams.get('new_web_builder') === 'true';
  const isDraft = searchParams.get('version') === 'draft';
  const isExampleSite = searchParams.get('example_site') === 'true';
  const showExampleSite = isDraft && isExampleSite;
  const { instanceToFetch, isInitialLoadFromSwarm, canShowCookieBanner } = previewAllowedCheck(request);
  const SWARM_TOKEN_SECRET = env.SWARM_TOKEN_SECRET || '';

  // Set the session tokens prior to making any requests to swarm
  // When a user is logged in, we set the session.
  // A user can be defined in two separate ways: (1) Subscription or (2) Admin User. We check for both.
  // An Admin can be logged in as a subscription user and an admin user at the same time.
  // If there is no logged in user, we should avoid using a Set-Cookie header
  // This will prevent us from being able to cache pages for logged out users
  let redirectFromToken = false;
  if (token || adminToken) {
    if (token) {
      redirectFromToken ||= updateSessionToken(session, 'token', token, SWARM_TOKEN_SECRET);
    }
    if (adminToken) {
      redirectFromToken ||= updateSessionToken(session, 'admin_token', adminToken, SWARM_TOKEN_SECRET);
    }
  }

  // TODO: Move the rest of the relevant data about web themes out of publication request into
  // The web theme request. Step 1 was just to clean up the logic around rootStyles.
  const [publication, webTheme, subscription, admin, webThemev2] = await Promise.all([
    getPublication(request, session),
    getWebTheme(request, session),
    getSubscriptionProfile(request, session, token),
    getAdmin(request, session),
    getWebThemev2(request, session, {
      status: instanceToFetch,
    }),
  ]);

  Sentry.setContext('publication', {
    id: publication.id,
    name: publication.name,
  });

  // Check if the request host is different than the custom domain configured
  //   for the current publication (if any). If it is, we will redirect to the
  //   custom domain, retaining any parameters. This is useful for redirecting
  //   beehiiv publication subdomains to custom domains.
  //
  //   Example: get mypublication.beehiiv.com/p/test?foo=bar
  //     => 301 https://blog.mypublication.com/p/test?foo=bar
  // Make sure to strip the port from the host name if present
  const hostName = (request.headers.get('x-forwarded-host') || url.hostname).split(':')[0];

  const configuredDomain = publication.configured_domain;
  if (configuredDomain && configuredDomain !== hostName) {
    url.protocol = 'https:'; // Ensure the protocol is https
    url.hostname = configuredDomain;
    throw redirect(url.href, {
      status: 301,
      headers: {
        // expire any session cookie on wrong domain
        'Set-Cookie': await authSession(request).destroySession(session),
      },
    });
  }

  // Set Data to be returned to client
  const jsonData = {
    canShowCookieBanner,
    publication: showExampleSite ? getExamplePublication(publication) : publication,
    rootStyles: webTheme?.root_styles || getRootStyles(publication),
    isLoggedIn: Boolean(subscription),
    admin,
    subscription: subscription,
    isProdEnv: process.env.NODE_ENV === 'production',
    facebookAppId: env.FACEBOOK_APP_ID,
    currentPath: url.pathname,
    hasWebBuilderV2: webThemev2?.feature_enabled || forceNewWebBuilder,
    webThemev2: webThemev2?.web_theme,
    signupWidgetConfigs: getSignupWidgetConfigs({
      webTheme: webThemev2?.web_theme,
    }),
    isInitialLoadFromSwarm,
    showLoggedInToast: Boolean(token),
    hostName,
    // include latest release in root to compare with initial document request
    // in AppGlobals. If different, for a refresh
    SENTRY_RELEASE: appGlobals.SENTRY_RELEASE,
    noScript,
  };

  if (subscription) {
    Sentry.setContext('subscription', {
      id: subscription.id,
      tier: subscription.tier,
      status: subscription.status,
      email: subscription.email,
      subscriberId: subscription.subscriber_id,
    });
  }

  // PAGE SPECIFIC REDIRECTS
  // We do this to avoid additional fetch requests in child loaders

  // PrivatePubs redirect: This specific redirect is for the /p/:slug route and checks if the publication is private or requires subscriber approval
  const isPrivate = publication.private;
  const requireSubscriberApproval = publication.require_subscriber_approval;
  const shouldRedirect = !subscription && (isPrivate || requireSubscriberApproval);
  const isPostsPage = url.pathname?.startsWith('/p/');
  const isDraftPost = url.searchParams.get('draft') === 'true';
  if (shouldRedirect && isPostsPage && !isDraftPost) {
    return redirect('/');
  }

  // Login page redirect: This specific redirect is for the /login route and checks if the user is already logged in
  const isUserLoggedIn = Boolean(subscription);
  const isLoginPage = url.pathname?.startsWith('/login');
  if (isUserLoggedIn && isLoginPage) {
    return redirect('/');
  }

  const cookieHeader = request.headers.get('Cookie');
  const sessionCookieExists = cookieHeader?.includes('_orchid_session');
  const sessionTokenDoesNotExist = !session.get('token') && !session.get('admin_token');

  const getHeaders = async () => {
    const headers = new Headers();
    headers.append('Set-Cookie', await cookieHeaders());
    headers.append('Cache-Control', 'no-store');
    return headers;
  };

  if (!isLoginPage && (token || adminToken || subscription)) {
    if (redirectFromToken) {
      // remove the token from the url
      url.searchParams.delete('jwt_token');
      url.searchParams.delete('admin_token');
      // redirect to same-origin url with updated session
      throw redirect(`${url.pathname}${url.search}`, {
        headers: await getHeaders(),
      });
    }
  } else if (sessionCookieExists && sessionTokenDoesNotExist) {
    return json(jsonData, {
      headers: {
        'Set-Cookie': await authSession(request).destroySession(session),
      },
    });
  }
  // If the user is logged in, we should refresh the session cookie
  return json(jsonData, {
    headers: await getHeaders(),
  });
};

export const clientLoader = async ({ serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<RootLoaderData>();

  // Validate that we received the expected data from the server
  // If publication is missing (e.g., due to network issues or timeouts), throw RefreshPageError
  // This will show a user-friendly error boundary asking the user to refresh
  if (!serverData?.publication) {
    throw new Error('RefreshPageError');
  }

  return serverData;
};

clientLoader.hydrate = true;

export interface RootLoaderData {
  publication: Publication;
  recommendingPublication: Pick<Publication, 'id' | 'name'>;
  rootStyles: string;
  isLoggedIn: boolean;
  subscription: Subscription;
  admin: Admin;
  webThemev2: WebThemeV2;
  hasWebBuilderV2: boolean;
  isProdEnv: boolean;
  facebookAppId: string;
  currentPath: string;
  signupWidgetConfigs: SignupStyles;
  showLoggedInToast: boolean;
  isInitialLoadFromSwarm: boolean;
  canShowCookieBanner: boolean;
  SENTRY_RELEASE: string;
  noScript: boolean;
}

const App = () => {
  const {
    isLoggedIn,
    subscription,
    admin,
    publication,
    rootStyles,
    isProdEnv,
    webThemev2,
    hasWebBuilderV2,
    currentPath,
    showLoggedInToast,
    isInitialLoadFromSwarm,
    signupWidgetConfigs,
    canShowCookieBanner,
    SENTRY_RELEASE,
  } = useTypedLoaderData<RootLoaderData>();
  useSWEffect();

  // [WEB BUILDER] Handles communication with the parent app when rendered in swarm via an iframe
  useWebBuilderCommunicator({
    isInitialLoadFromSwarm,
    publicationId: publication.id,
  });

  // [USER STATUS] Sets the user status based on the subscription tier and whether or not they are logged in
  const { isLoggedInOrSubscribed, isPremiumSubscription } = useUserStatus({
    isLoggedIn,
    tier: subscription?.tier,
  });

  // [COOKIES] Sets the user-specific cookies
  // Note: Only set cookies client side when users are not in a session
  // The Set-cookie header server side will impact our ability to cache web pages for performance
  // *cookieSet* is a way of letting us know that app has mounted and we have retrieved all available cookies
  const { cookies, setCookie, cookiesSet, removeCookie } = useCookies({
    initialCookies: {
      popup_closed: isLoggedInOrSubscribed,
      email_capture_disabled: isLoggedInOrSubscribed,
      active_upgrade: isPremiumSubscription,
    },
  });

  // [LANGUAGE] Sets the language for the site based on the publication language
  useSiteLanguage({ language: publication?.language, setCookie });

  // [VISIT TOKEN] Creates a visit token for the user
  // We use visit tokens to track user site visits + page views and more
  // It is critical to many parts of our web analytics that this is set before signups and page views
  const visitTokenCookie = Boolean(cookies.visit_token);
  useCreateVisitToken({
    shouldCreateVisitToken: cookiesSet && !visitTokenCookie,
    setCookie,
  });

  useSetLastViewedResource({ setCookie, cookiesSet });

  // [REMOVE PARAMS] Removes query params from the url
  // We occasionally user query params to pass state between pages
  // To prevent params from impacting state more than once, clear them from the url
  useRemoveParam('jwt_token');
  useRemoveParam('admin_token');
  useRemoveParam('status');
  useRemoveParam('upgraded');
  useRemoveParam('close_draft_preview');

  // [NOTICATIONS] Handles the notifications for the site
  useJSDisabledSubmissionToasts();
  useRedirectToasts();
  useNotifications({ showLoggedInToast: isLoggedIn && showLoggedInToast });
  usePushNotification({ icon: publication?.logo?.thumb?.url });
  // [LAYOUT] Handles the layout of the site
  const { isNavShowing, isFooterShowing } = useLayout();
  const { progress, redirects, currentStep } = useSignupRedirects(publication);

  const displayMode = useDisplayMode();

  // check latest release in the root loader with AppGlobals SENTRY_RELEASE
  // from initial document request. If different, reload the page.
  useEffect(() => {
    // only check in standalone mode (PWA)
    if (displayMode !== 'standalone') return;

    if (SENTRY_RELEASE !== window.__AppGlobals__?.SENTRY_RELEASE) {
      console.log(
        `Reloading page due to new release. Old release: ${window.__AppGlobals__?.SENTRY_RELEASE}, New release: ${SENTRY_RELEASE}`,
      );
      window.location.reload();
    }
  }, [SENTRY_RELEASE, displayMode]);

  // @ts-expect-error
  const match = useMatches().find((match) => match?.handle?.layout) as any;
  const CustomLayout = match?.handle?.layout;
  if (CustomLayout) return CustomLayout;

  return (
    <Document
      publication={publication}
      rootStyles={rootStyles}
      isProdEnv={isProdEnv}
      cookies={cookies}
      cookiesSet={cookiesSet}
    >
      <WebThemeProvider webTheme={webThemev2} isEnabled={hasWebBuilderV2}>
        <PageProvider
          cookies={cookies}
          setCookie={setCookie}
          cookiesSet={cookiesSet}
          removeCookie={removeCookie}
          visitToken={cookies.visit_token}
          referralId={cookies.referral_id}
          currentPath={currentPath}
        >
          <ClientOnly>{() => <BirdieLoader publication={publication} />}</ClientOnly>
          {admin && (
            <AdminBadge setCookie={setCookie} justLoggedIn={cookiesSet && !cookies.admin_logged_in && Boolean(admin)} />
          )}

          <GlobalModals publication={publication} signupWidgetConfigs={signupWidgetConfigs} />
          <Toaster position="top-center" />
          {canShowCookieBanner ? (
            <BeehiivHoney publication={publication} cookies={cookies} setCookie={setCookie} />
          ) : null}
          {isNavShowing && !progress ? (
            hasWebBuilderV2 ? (
              <NavTemplates
                publication={publication}
                subscription={subscription}
                isLoggedin={isLoggedIn}
                isAdminLoggedIn={Boolean(admin)}
              />
            ) : (
              <Layout.Nav publication={publication} isLoggedIn={isLoggedIn} subscription={subscription} />
            )
          ) : (
            <></>
          )}
          {progress ? (
            <Layout.SignupFlowNav
              progress={progress}
              redirects={redirects}
              currentStep={currentStep}
              publication={publication}
            />
          ) : null}
          <Layout.Main noLayout={!isNavShowing && !isFooterShowing}>
            <Outlet
              context={{
                publicationId: publication?.id,
                cookies,
                setCookie,
                cookiesSet,
                removeCookie,
                visitToken: cookies.visit_token,
                referralId: cookies.referral_id,
                admin,
                isLoggedIn,
              }}
            />
          </Layout.Main>
          {isFooterShowing ? (
            hasWebBuilderV2 ? (
              <Footer />
            ) : (
              <Layout.SignupFooterv2 publication={publication} subscription={subscription} />
            )
          ) : (
            <></>
          )}
          {publication?.web_template?.pwa_enabled && (
            <>
              <InstallPrompt />
              <EnablePushNotificationPrompt />
            </>
          )}
        </PageProvider>
      </WebThemeProvider>
    </Document>
  );
};

const SentryApp = Sentry.withSentry(App);
// biome-ignore lint/style/useComponentExportOnlyModules: Sentry wrapped component export
export default SentryApp;

export function ErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    if (isRouteErrorResponse(error)) return;
    Sentry.captureException(error, {
      tags: {
        keyword: 'remix-error-boundary',
      },
    });
  }, [error]);

  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 400: {
        return (
          <Document>
            <FallBack errorCode="400" publicationPresent={false} />
          </Document>
        );
      }
      case 404: {
        return (
          <Document>
            {error.data === 'DirectLinkExpired' ? (
              <FallBack
                customTitle={'Uh oh, it looks like this link is expired.'}
                customDescription={<>The Publication needs to update a few things on their end to resume.</>}
                errorCode="404"
                publicationPresent={false}
              />
            ) : (
              <FallBack errorCode="404" publicationPresent={false} />
            )}
          </Document>
        );
      }
      default: {
        return (
          <Document>
            <FallBack errorCode={`${error.status}`} error={error as unknown as AxiosError<unknown, any>} />
          </Document>
        );
      }
    }
  }

  // default to client error
  let errorCode = '0';
  if (error instanceof Error) {
    let errorMessage = error.message ?? '';
    // if error message is a json string, parse it and extract status and message
    // this is set in `handleError` function in entry.server.tsx
    if (errorMessage.startsWith('{')) {
      try {
        const errorJson = JSON.parse(errorMessage);
        errorCode = errorJson.status ?? '0';
        errorMessage = errorJson.message ?? '';
      } catch (_e) {
        // do nothing
      }
    }
  }

  // To make debugging easier in development.
  if (process.env.NODE_ENV === 'development') {
    const unknownError: any = error;

    return (
      <Document title={unknownError?.name}>
        <div className="p-6 m-6 space-y-4 overflow-scroll text-sm text-red-600 rounded bg-red-50">
          <h1 className="text-lg font-semibold">Exception caught by root error boundary</h1>
          <p>{unknownError?.message}</p>
          <pre>{unknownError?.stack}</pre>
        </div>
      </Document>
    );
  }

  return (
    <Document title="Error!">
      <FallBack errorCode={errorCode} error={error as AxiosError<unknown, any>} />
    </Document>
  );
}

export const shouldRevalidate = shouldRevalidateLoaderData;
